/**
 * Authoring (producer-side) construction of a `prompt-execution` strategy job.
 * Builds the opaque input bundle Loom enqueues, stamping the shuttle-strategy
 * header so the Shuttle can demux it (ADR 0005). The Loom service and Jobs treat
 * the result as opaque inputs.
 */

import type { EnqueueJobOptions, JobInputOrigin, PoolRef } from '@whittlelabs/jobs';
import type { PromptExecutionSpec, PromptExecutionRendering } from '../runtime/types';
import { shuttleStrategyInput, PROMPT_EXECUTION_STRATEGY } from '../runtime/strategy';
import type { JSONSchema7 } from 'json-schema';

export interface EnqueuePromptExecutionOptions {
  pool: PoolRef;
  rendering: PromptExecutionRendering;
  /** Required when `rendering === 'producer'`. */
  prompt?: string;
  /** Required when `rendering === 'subscriber'`. */
  template?: string;
  vars?: Record<string, string | number | boolean>;
  artifacts?: Array<{
    label: string;
    origin: JobInputOrigin;
    payload: Record<string, unknown>;
  }>;
  output: { label: string; schema: JSONSchema7 };
  /**
   * Materialized-checkout request for executors with filesystem access
   * (see `PromptExecutionWorkspace`). Rides the spec verbatim.
   */
  workspace?: PromptExecutionSpec['workspace'];
  providerHints?: PromptExecutionSpec['providerHints'];
  costCapHint?: PromptExecutionSpec['costCapHint'];
  /** Opaque live-progress routing token; rides the spec verbatim (see spec). */
  progressChannel?: string;
  clientRef?: string;
  clientContext?: Record<string, unknown>;
  priority?: number;
  tags?: string[];
  maxAttempts?: number;
  deadlineDuration?: string;
  signal?: AbortSignal;
}

/**
 * Build the canonical prompt-execution input rows. The first row is the
 * shuttle-strategy header; the rest are the strategy's bundle (spec, prompt or
 * template+vars, artifacts).
 */
export function buildPromptExecutionInputs(
  options: EnqueuePromptExecutionOptions,
): EnqueueJobOptions['inputs'] {
  const spec: PromptExecutionSpec = {
    rendering: options.rendering,
    outputLabel: options.output.label,
    outputSchema: options.output.schema,
  };
  if (options.workspace) spec.workspace = options.workspace;
  if (options.providerHints) spec.providerHints = options.providerHints;
  if (options.costCapHint) spec.costCapHint = options.costCapHint;
  if (options.progressChannel !== undefined) spec.progressChannel = options.progressChannel;

  if (options.rendering === 'producer' && !options.prompt) {
    throw new Error("rendering='producer' requires `prompt`");
  }
  if (options.rendering === 'subscriber' && !options.template) {
    throw new Error("rendering='subscriber' requires `template`");
  }

  const inputs: EnqueueJobOptions['inputs'] = [
    shuttleStrategyInput(PROMPT_EXECUTION_STRATEGY),
    { label: 'spec', origin: 'raw', payload: { spec } as unknown as Record<string, unknown> },
  ];

  if (options.rendering === 'producer') {
    inputs.push({ label: 'prompt', origin: 'raw', payload: { text: options.prompt! } });
  } else {
    inputs.push({ label: 'template', origin: 'raw', payload: { text: options.template! } });
    if (options.vars) {
      inputs.push({ label: 'vars', origin: 'raw', payload: options.vars as Record<string, unknown> });
    }
  }

  if (options.artifacts) {
    for (const artifact of options.artifacts) {
      inputs.push({
        label: 'artifacts',
        origin: artifact.origin,
        payload: { label: artifact.label, ...artifact.payload },
      });
    }
  }

  return inputs;
}
